import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, TrendingUp, Shield, Repeat, 
  Search, ChevronRight, Star, CheckCircle, Users, DollarSign, Zap,
  BadgeCheck, Clock, Sparkles, ArrowUpRight
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ListingCard from '../components/ListingCard';
import { api } from '../App';
import { CATEGORIES as ALL_CATEGORIES, getCategoryLabel } from '../lib/categories';

const HOMEPAGE_CATEGORIES = ALL_CATEGORIES.filter(c => c.value !== 'other');

const HOW_IT_WORKS = [
  { 
    step: '01', 
    icon: Sparkles, 
    title: 'List Your Item', 
    description: 'Create a listing in minutes. Add photos, set your price, or enable trading.',
    color: 'text-gold'
  },
  { 
    step: '02', 
    icon: Users, 
    title: 'Connect With Buyers', 
    description: 'Receive offers, negotiate trades, and chat with serious collectors.',
    color: 'text-emerald-400'
  },
  { 
    step: '03', 
    icon: DollarSign, 
    title: 'Sell or Trade', 
    description: 'Close the deal your way. Accept cash, trades, or a combination of both.',
    color: 'text-blue-400'
  },
];

const TESTIMONIALS = [
  {
    name: 'Michael R.',
    role: 'Coin Collector',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100',
    text: 'Finally a platform that understands collectors. The trading feature is a game-changer.',
    rating: 5
  },
  {
    name: 'Sarah K.',
    role: 'Gold Investor',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100',
    text: 'Sold my first gold bar within 48 hours. The buyer verification gave me peace of mind.',
    rating: 5
  },
  {
    name: 'David L.',
    role: 'Trusted Dealer',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
    text: "Best marketplace for serious dealers. My sales have increased 3x since joining.",
    rating: 5
  },
];

const PLATFORM_STATS = [
  { value: '500+', label: 'Active Users', icon: Users },
  { value: '$166K+', label: 'Total Traded', icon: DollarSign },
  { value: '250+', label: 'Completed Deals', icon: CheckCircle },
  { value: '4.9/5', label: 'Avg. Rating', icon: Star },
];

const RECENTLY_SOLD = [
  { title: '2023 Bowman Chrome Wander Franco PSA 10', price: 4250, category: 'sports_cards', time: '2 hours ago' },
  { title: '10oz Gold Bar - PAMP Suisse', price: 19800, category: 'precious_metals', time: '5 hours ago' },
  { title: 'Base Set Charizard Holo PSA 9', price: 12500, category: 'tcg', time: '1 day ago' },
  { title: '1921 Morgan Silver Dollar MS-65', price: 3200, category: 'coins', time: '1 day ago' },
];

export default function HomePage() {
  const [featuredListings, setFeaturedListings] = useState([]);
  const [categoryStats, setCategoryStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [listingsRes, statsRes] = await Promise.all([
          api.get('/listings/featured'),
          api.get('/categories/stats')
        ]);
        setFeaturedListings(listingsRes.data);
        setCategoryStats(statsRes.data);
      } catch {
        // silently handled
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-surface-base">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-[95vh] flex items-center overflow-hidden bg-surface-base">
        <div 
          className="absolute inset-0 bg-contain bg-no-repeat bg-right-bottom opacity-60 sm:opacity-70 pointer-events-none"
          style={{ 
            backgroundImage: `url(/ch-logo.png)`,
            backgroundPosition: 'right 5% top 40%',
            backgroundSize: 'min(55%, 700px)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-surface-base via-surface-base/90 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-surface-base via-transparent to-surface-base/50" />
        
        <div className="relative max-w-7xl mx-auto pl-4 sm:pl-6 lg:pl-8 pr-4 py-20">
          <div className="max-w-3xl -ml-2 sm:-ml-4 lg:-ml-8">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/20 mb-8 animate-fade-in">
              <Shield className="w-4 h-4 text-gold" />
              <span className="text-sm text-gold font-medium">Secure & Verified Transactions</span>
            </div>

            <h1 className="font-clash text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight text-white mb-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
              The Marketplace for{' '}
              <span className="gold-text">Real Assets</span>
            </h1>
            
            <p className="text-xl text-zinc-300 mb-4 max-w-2xl leading-relaxed animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Buy, sell, and trade coins, precious metals, sports cards, TCG cards, and collectibles.
            </p>
            
            <p className="text-base text-zinc-400 mb-10 max-w-xl mx-auto animate-fade-in hidden sm:block" style={{ animationDelay: '0.3s' }}>
              The trusted platform where collectors and investors discover, negotiate, and transact on 
              high-value tangible assets. Trade with confidence.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <Link
                to="/marketplace"
                data-testid="browse-marketplace-btn"
                className="btn-gold px-8 py-4 text-center font-semibold uppercase tracking-wider text-sm flex items-center justify-center gap-2 group"
              >
                Browse Marketplace
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link
                to="/create-listing"
                data-testid="start-selling-btn"
                className="btn-secondary px-8 py-4 text-center uppercase tracking-wider text-sm flex items-center justify-center gap-2"
              >
                Start Selling
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/learn"
                data-testid="learn-btn"
                className="px-8 py-4 text-center text-sm text-zinc-400 hover:text-gold transition-colors flex items-center justify-center gap-2 border border-border-subtle hover:border-gold/30"
              >
                Learn How It Works
              </Link>
            </div>

            {/* Quick Stats */}
            <div className="flex flex-wrap gap-8 mt-16 pt-8 border-t border-border-subtle animate-fade-in" style={{ animationDelay: '0.5s' }}>
              {PLATFORM_STATS.slice(0, 3).map((stat) => (
                <div key={stat.label} className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gold/10 flex items-center justify-center">
                    <stat.icon className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="font-clash text-xl font-semibold text-white">{stat.value}</p>
                    <p className="text-xs text-zinc-500">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-6 bg-surface-card border-y border-border-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            <div className="flex items-center gap-2 text-sm text-zinc-400">
              <BadgeCheck className="w-5 h-5 text-emerald-500" />
              <span>Verified Sellers</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-400">
              <Shield className="w-5 h-5 text-blue-500" />
              <span>Secure Payments</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-400">
              <Repeat className="w-5 h-5 text-gold" />
              <span>Trade-Enabled</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-400">
              <Star className="w-5 h-5 text-yellow-500" />
              <span>Buyer Protection</span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Listings */}
      <section className="py-20 bg-surface-base">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-5 h-5 text-gold" />
                <p className="text-xs tracking-[0.2em] uppercase text-gold font-semibold">
                  Hot Right Now
                </p>
              </div>
              <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white">
                Featured Listings
              </h2>
            </div>
            <Link 
              to="/marketplace" 
              className="hidden sm:flex items-center gap-2 text-zinc-400 hover:text-gold transition-colors group"
            >
              View All <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-[4/5] bg-surface-card animate-pulse" />
              ))}
            </div>
          ) : featuredListings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredListings.slice(0, 8).map((listing, idx) => (
                <ListingCard key={listing.id} listing={listing} index={idx} size="large" />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-surface-card border border-border-subtle">
              <Sparkles className="w-12 h-12 text-gold mx-auto mb-4" />
              <p className="text-zinc-400 mb-4">No listings yet. Be the first to sell!</p>
              <Link to="/create-listing" className="btn-gold px-6 py-3 inline-block">
                Create Listing
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Trading Feature Highlight */}
      <section className="py-20 bg-gradient-to-b from-surface-base to-surface-card relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ 
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(212,175,55,0.1) 10px, rgba(212,175,55,0.1) 20px)'
          }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/20 mb-6">
                <Repeat className="w-4 h-4 text-gold" />
                <span className="text-sm text-gold font-medium">Trading Feature</span>
              </div>
              <h2 className="font-clash text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-6">
                Trade Assets,<br />
                <span className="gold-text">Not Just Cash</span>
              </h2>
              <p className="text-lg text-zinc-400 mb-8 leading-relaxed">
                Our unique trading system lets you propose item-for-item swaps, or combine items with cash. 
                It's how real collectors do business.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Propose trades on any trade-enabled listing',
                  'Combine multiple items + cash in one offer',
                  'Negotiate directly with sellers',
                  'All trades protected by our trust system'
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-zinc-300">
                    <CheckCircle className="w-5 h-5 text-gold flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/marketplace?trade=true"
                className="btn-gold px-6 py-3 inline-flex items-center gap-2"
              >
                Browse Trade-Enabled Items
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-square bg-surface-elevated border border-border-subtle p-8 relative">
                <img
                  src="https://images.unsplash.com/photo-1610375461246-83df859d849d?w=600"
                  alt="Trading"
                  className="w-full h-full object-cover"
                />
                <div className="absolute -bottom-4 -right-4 bg-gold text-black px-6 py-3 font-clash font-semibold">
                  <Repeat className="w-5 h-5 inline mr-2" />
                  TRADE ENABLED
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-surface-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.2em] uppercase text-gold font-semibold mb-3">
              Explore Categories
            </p>
            <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white">
              Find Your Next Asset
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {HOMEPAGE_CATEGORIES.map((category, idx) => {
              const stat = categoryStats.find(s => s.category === category.value);
              const CatIcon = category.icon;
              return (
                <Link
                  key={category.value}
                  to={`/marketplace?category=${category.value}`}
                  data-testid={`category-${category.value}`}
                  className="group relative aspect-[4/5] overflow-hidden bg-surface-elevated border border-border-subtle hover:border-gold/50 transition-all duration-500"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <img
                    src={category.image}
                    alt={category.label}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-surface-base via-surface-base/60 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="w-12 h-12 bg-gold/10 border border-gold/20 flex items-center justify-center mb-4 group-hover:bg-gold/20 transition-colors">
                      <CatIcon className="w-6 h-6 text-gold" />
                    </div>
                    <h3 className="font-clash text-xl font-medium text-white mb-1 group-hover:text-gold transition-colors">
                      {category.label}
                    </h3>
                    <p className="text-sm text-zinc-400 mb-2">{category.description}</p>
                    <p className="text-xs text-zinc-500">
                      {stat?.count || 0} active listings
                    </p>
                  </div>
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowUpRight className="w-6 h-6 text-gold" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Recently Sold / Market Activity */}
      <section className="py-20 bg-surface-base">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            <p className="text-xs tracking-[0.2em] uppercase text-emerald-500 font-semibold">
              Market Activity
            </p>
          </div>
          <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white mb-10">
            Recently Sold
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {RECENTLY_SOLD.map((item) => (
              <div 
                key={item.title} 
                className="p-5 bg-surface-card border border-border-subtle hover:border-emerald-500/30 transition-colors group"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs text-emerald-500 font-medium">SOLD</span>
                  <span className="text-xs text-zinc-500 ml-auto">{item.time}</span>
                </div>
                <h4 className="font-medium text-white mb-2 line-clamp-2 group-hover:text-gold transition-colors">
                  {item.title}
                </h4>
                <p className="font-clash text-xl font-semibold text-emerald-500">
                  ${item.price.toLocaleString()}
                </p>
                <p className="text-xs text-zinc-500 mt-1">
                  {getCategoryLabel(item.category)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works / Seller Incentives */}
      <section className="py-20 bg-surface-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-gold font-semibold mb-3">
              Start Selling Today
            </p>
            <h2 className="font-clash text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-4">
              Turn Your Collection Into Cash
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              List your items in minutes and connect with thousands of serious buyers and collectors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {HOW_IT_WORKS.map((step) => (
              <div 
                key={step.step}
                className="relative p-8 bg-surface-base border border-border-subtle group hover:border-gold/30 transition-all duration-300"
              >
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gold text-black font-clash font-bold text-lg flex items-center justify-center">
                  {step.step}
                </div>
                <div className={`w-14 h-14 flex items-center justify-center bg-surface-elevated mb-6 ${step.color}`}>
                  <step.icon className="w-7 h-7" />
                </div>
                <h3 className="font-clash text-xl font-medium text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-zinc-400 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/create-listing"
              className="btn-gold px-10 py-4 inline-flex items-center gap-2 text-lg"
            >
              Start Selling Now
              <ArrowRight className="w-5 h-5" />
            </Link>
            <p className="text-sm text-zinc-500 mt-4">No listing fees for your first 5 items</p>
          </div>
        </div>
      </section>

      {/* Social Proof / Testimonials */}
      <section className="py-20 bg-surface-base">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.2em] uppercase text-gold font-semibold mb-3">
              Trusted by Collectors
            </p>
            <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white">
              What Our Users Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {TESTIMONIALS.map((testimonial) => (
              <div 
                key={testimonial.name} 
                className="p-8 bg-surface-card border border-border-subtle hover:border-gold/30 transition-colors"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-gold fill-gold" />
                  ))}
                </div>
                <p className="text-zinc-300 mb-6 leading-relaxed italic">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name}
                    className="w-12 h-12 object-cover"
                  />
                  <div>
                    <p className="font-medium text-white">{testimonial.name}</p>
                    <p className="text-sm text-zinc-500 flex items-center gap-1">
                      {testimonial.role === 'Trusted Dealer' && (
                        <BadgeCheck className="w-4 h-4 text-emerald-500" />
                      )}
                      {testimonial.role}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Platform Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {PLATFORM_STATS.map((stat) => (
              <div 
                key={stat.label}
                className="p-6 bg-surface-card border border-border-subtle text-center group hover:border-gold/30 transition-colors"
              >
                <stat.icon className="w-8 h-8 text-gold mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <p className="font-clash text-3xl font-semibold text-white mb-1">{stat.value}</p>
                <p className="text-sm text-zinc-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wanted Section CTA */}
      <section className="py-20 bg-gradient-to-b from-surface-card to-surface-base relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1612734425688-d7a59377e5f7?w=1920" 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/20 mb-6">
              <Search className="w-4 h-4 text-gold" />
              <span className="text-sm text-gold font-medium">Can't Find What You Need?</span>
            </div>
            <h2 className="font-clash text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-6">
              Post a Wanted Listing
            </h2>
            <p className="text-lg text-zinc-400 mb-10">
              Describe what you're looking for and let sellers come to you. Our matching system connects 
              you with the right collectors.
            </p>
            <Link
              to="/wanted"
              data-testid="explore-wanted-btn"
              className="btn-gold px-8 py-4 inline-flex items-center gap-2 uppercase tracking-wider text-sm"
            >
              Explore Wanted <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
